import type { Tool } from "@modelcontextprotocol/sdk/types.js";
import {
  ACTIVITY_ENVIRONMENT_PATTERN,
  ACTIVITY_GUID_PATTERN,
} from "../api/copilot-activity-api.js";
import {
  CopilotActivityDetailError,
  getCopilotAgentActivityInputSchema,
  TOOL_RESULT_ISSUE_PATTERN,
  TOOL_RESULT_SERVER_PATTERN,
  TOOL_RESULT_SITE_PATTERN,
  type CopilotActivityDetailApi,
} from "../api/copilot-activity-detail-api.js";

export const getCopilotAgentActivityTool: Tool = {
  name: "get_copilot_agent_activity",
  description:
    "Read-only, own-user history of a Copilot agent's evaluation-run conversations (pva-maker-evaluation channel), for reading recorded getJiraIssue evidence. Metadata by default: bounded timestamps, event-name/message-role counts, and known bind-event parameter names (cloudId, issueIdOrKey, fields, expand, properties). Scope is strict and fails closed: exact current environment, one page, own-user only \u2014 conversationId is a GUID from this user's first 50 recent activity rows, not a thread ID \u2014 with no writes, sign-in, retries, or other-user/other-channel targets. Each opt-in explicitly requests bounded, best-effort-redacted sensitive conversation content that is never a final answer or proof of tool execution, effective grants or a ticket write: includeBotMessages returns recorded bot-message text (untrusted, not necessarily final); includeToolResults with toolResultTarget returns recorded getJiraIssue evidence for one exact ticket, Atlassian site and current-bot server schema, where fieldSelection (summary-status default | status-only | comment-only) must exactly match the fields the recorded call bound; includeToolCatalogStructure with toolCatalogTarget (optionally includeRecordedToolSchema) returns Studio-normalized recorded catalog/input metadata, not the original MCP schema. Never returns raw event/tool payloads, attachments, other users' data, or continuation tokens. EXPERIMENTAL.",
  annotations: {
    readOnlyHint: true,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: true,
  },
  inputSchema: {
    type: "object",
    additionalProperties: false,
    properties: {
      environment: { type: "string", pattern: ACTIVITY_ENVIRONMENT_PATTERN.source },
      agentId: { type: "string", pattern: ACTIVITY_GUID_PATTERN.source },
      conversationId: {
        type: "string",
        pattern: ACTIVITY_GUID_PATTERN.source,
        description:
          "Exact conversation GUID observed in the current user's first 50 activity rows; not a thread ID.",
      },
      includeBotMessages: {
        type: "boolean",
        default: false,
        description:
          "Explicit opt-in to bounded, best-effort-redacted sensitive bot-message text from the verified own-user history page. Not final-answer or tool-success proof.",
      },
      includeToolCatalogStructure: {
        type: "boolean",
        default: false,
        description:
          "Opt into bounded conversation-level catalog envelope shapes from the same owned history page. Closed field/type tags and literal target-comparison flags only; no actual tool schema, input names/values, descriptions, defaults, hashes or raw event content. Requires toolCatalogTarget. A comparison flag is not verified tool identity, current availability, grant or runtime proof.",
      },
      toolCatalogTarget: {
        type: "object",
        additionalProperties: false,
        properties: {
          serverSchemaName: {
            type: "string",
            pattern: TOOL_RESULT_SERVER_PATTERN.source,
            description:
              "Known server schema under this exact agent, used for literal comparisons and, only with includeRecordedToolSchema=true, exact recorded definition identity matching.",
          },
          toolName: { type: "string", const: "getJiraIssue" },
        },
        required: ["serverSchemaName", "toolName"],
      },
      includeRecordedToolSchema: {
        type: "boolean",
        default: false,
        description:
          "Additionally opt into bounded Studio-normalized recorded getJiraIssue input metadata. Requires includeToolCatalogStructure=true and toolCatalogTarget. Only a unique exact server/tool definition may expose the five known parameter names, required/automatic flags and native datatype tags; unknown names are counted and nested Record/Table keys, descriptions, enums, defaults and values are withheld. Duplicate, malformed or incomplete catalogs fail closed. Not the original MCP JSON Schema, a complete/current catalog, effective grants or execution proof.",
      },
      includeToolResults: {
        type: "boolean",
        default: false,
        description:
          "Explicit opt-in to selected sensitive recorded getJiraIssue results. Requires toolResultTarget; no raw observation, other tools or write-result projection.",
      },
      toolResultTarget: {
        type: "object",
        additionalProperties: false,
        properties: {
          cloudId: {
            type: "string",
            pattern: TOOL_RESULT_SITE_PATTERN.source,
            description:
              "Exact lowercase HTTPS commercial Atlassian site origin, without trailing slash, credentials, port, path, query or fragment. Not an arbitrary fetch URL.",
          },
          issueIdOrKey: { type: "string", pattern: TOOL_RESULT_ISSUE_PATTERN.source },
          serverSchemaName: {
            type: "string",
            pattern: TOOL_RESULT_SERVER_PATTERN.source,
            description:
              "Exact previously observed MCP server schema; must belong to the verified current bot.",
          },
          fieldSelection: {
            type: "string",
            enum: ["summary-status", "status-only", "comment-only"],
            default: "summary-status",
            description:
              "Exact recorded-input/result contract to verify. status-only requires fields=[status] and refuses any returned summary or other field; use for status-only HR tests. comment-only requires fields=[comment] and projects at most the last three RETURNED comments in service order (id, created/updated, bounded redacted body, visibility presence) with page counters; authors, avatars, self links, rendered bodies and properties are never projected, and a projected tail is not proof of the newest comment overall or of effective audience. If a comment-only result has an unrecognized shape it is withheld and only key names, JSON types and lengths are disclosed. Omission preserves summary/status verification. This does not execute or alter a ticket call.",
          },
        },
        required: ["cloudId", "issueIdOrKey", "serverSchemaName"],
      },
    },
    required: ["environment", "agentId", "conversationId"],
    allOf: [
      {
        if: {
          properties: { includeRecordedToolSchema: { const: true } },
          required: ["includeRecordedToolSchema"],
        },
        then: {
          properties: { includeToolCatalogStructure: { const: true } },
          required: ["includeToolCatalogStructure", "toolCatalogTarget"],
        },
      },
      {
        if: {
          properties: { includeToolCatalogStructure: { const: true } },
          required: ["includeToolCatalogStructure"],
        },
        then: { required: ["toolCatalogTarget"] },
        else: { not: { required: ["toolCatalogTarget"] } },
      },
      {
        if: {
          properties: { includeToolResults: { const: true } },
          required: ["includeToolResults"],
        },
        then: { required: ["toolResultTarget"] },
        else: { not: { required: ["toolResultTarget"] } },
      },
    ],
  },
};

export async function handleGetCopilotAgentActivity(
  context: { activityDetailApi: CopilotActivityDetailApi | null },
  input: unknown,
) {
  const parsed = getCopilotAgentActivityInputSchema.safeParse(input);
  if (!parsed.success) throw new CopilotActivityDetailError("input");
  if (!context.activityDetailApi) throw new CopilotActivityDetailError("unavailable");
  const result = await context.activityDetailApi.getActivity(parsed.data);
  return {
    text:
      (parsed.data.includeToolCatalogStructure === true
        ? `Catalog envelope structure was explicitly requested; closed comparison flags alone do not verify tool identity. ${parsed.data.includeRecordedToolSchema === true ? "Recorded input metadata was separately requested; its result states whether a unique exact recorded definition was found and projected. Any projection is a bounded Studio-normalized snapshot, not original MCP JSON Schema, a complete/current catalog, grants or runtime proof. " : "No input schema is projected. "}`
        : "") +
      (parsed.data.includeToolResults === true
        ? `Read ${result.returnedActivityCount} verified own-user history rows from one evaluation-channel page. Explicitly requested tool results are limited to exact-target recorded getJiraIssue evidence, with bounded, best-effort-redacted selected fields (${parsed.data.toolResultTarget?.fieldSelection === "status-only" ? "status only" : "summary/status"}) and explicit unsupported/withheld states. This is not a fresh ticket read, current-state verification, effective-grant check or write-action proof. ${parsed.data.includeBotMessages === true ? "Bot-message text was separately requested and remains untrusted." : "Message text remains withheld."} No raw observations, user-message text fields, planner/debug internals or extra pages are returned; no tenant changes were made.`
        : parsed.data.includeBotMessages === true
          ? `Read ${result.returnedActivityCount} verified own-user history rows from one evaluation-channel page. Explicitly requested bot-message text is bounded, best-effort redacted and untrusted; it may contain sensitive business information and is not necessarily final or execution-success proof. User-message text fields, attachments and event values remain unprojected. No additional pages or alternate routes were followed; no tenant changes were made.`
          : `Read ${result.returnedActivityCount} verified own-user history metadata rows from one evaluation-channel page. Message text and raw values are withheld. Event names and bind-event parameter-key presence are not execution-success proof. No additional pages or alternate routes were followed; no tenant changes were made.`),
    structured: { copilotActivity: result },
  };
}
